import {
  OperationModeType,
  AssetType,
  AssetSnapshot,
  Status,
  AssetsHistory,
  OperationModeSnapshot,
  ProducingOperationModeSnapshot,
  ProductionOperationModeSnapshot
} from '@hconnect/common/types'
import {Moment} from 'moment-timezone'

import {getRelevantHistoryEntry} from './history'

const toRecord = <T>(data: T[], property: keyof T): Record<string, T> => {
  return data.reduce(
    (acc, item) => {
      acc[String(item[property])] = item
      return acc
    },
    {} as Record<string, T>
  )
}

export const groupAndSortAssets = (assets: AssetSnapshot[]): Record<AssetType, AssetSnapshot[]> => {
  const assetsByType: Record<AssetType, AssetSnapshot[]> = {
    [AssetType.CementMill]: [],
    [AssetType.RawMill]: [],
    [AssetType.RotaryKiln]: [],
    [AssetType.CoalMill]: [],
    [AssetType.Crusher]: [],
    [AssetType.Other]: [],
    [AssetType.BaseLoad]: []
  }
  assets.forEach((asset) => {
    asset.operationModes = sortOperationModes(asset.operationModes)
    assetsByType[asset.type].push(asset)
  })
  Object.entries(assetsByType).forEach(([type, assets]) => {
    assetsByType[type as AssetType] = assets.sort((a, b) => a.name.localeCompare(b.name))
  })
  return assetsByType
}

export const sortAssets = (assets: AssetSnapshot[]): AssetSnapshot[] =>
  Object.values(groupAndSortAssets(assets)).flatMap((assets) => assets)

export const sortOperationModes = (
  operationModes: OperationModeSnapshot[]
): OperationModeSnapshot[] => {
  const modesByType: Record<OperationModeType, OperationModeSnapshot[]> = {
    [OperationModeType.Production]: [],
    [OperationModeType.Maintenance]: []
  }
  operationModes.forEach((mode) => modesByType[mode.type].push(mode))

  Object.entries(modesByType).forEach(
    ([type, modes]) =>
      (modesByType[type as OperationModeType] = modes.sort((a, b) => a.name.localeCompare(b.name)))
  )
  return Object.values(modesByType).flatMap((modes) => modes)
}

export const getOperationModeIdToAssetIdMap = (assets: AssetSnapshot[]) =>
  assets.reduce<Record<string, number>>((operationModeToAssetsMap, item) => {
    const operationModesToAsset = item.operationModes.reduce<Record<string, number>>(
      (acc, operationMode) => ({...acc, [operationMode.id]: item.id}),
      {}
    )
    return {
      ...operationModeToAssetsMap,
      ...operationModesToAsset
    }
  }, {})

export const guardProducingMode = (
  operationMode: OperationModeSnapshot
): operationMode is ProducingOperationModeSnapshot =>
  !!operationMode.recipeId && !!operationMode.throughput

export const guardProductionMode = (
  operationMode: OperationModeSnapshot
): operationMode is ProductionOperationModeSnapshot =>
  operationMode.type === OperationModeType.Production

export const getOperationModesById = (assets: AssetSnapshot[]) => {
  const operationModes = assets.flatMap((asset) => asset.operationModes)
  return toRecord(operationModes, 'id')
}

export const getAssetsById = (assets: AssetSnapshot[]) => toRecord(assets, 'id')

export const getBaseLoadPowerConsumptionFromAssets = (assets: AssetSnapshot[]) => {
  const baseLoadAssets = assets.filter((asset) => asset.type === AssetType.BaseLoad)
  if (baseLoadAssets.length !== 1) {
    throw new Error('BUG: Precisely one base load asset should be configured')
  }
  const baseloadOperationModes = baseLoadAssets[0].operationModes.filter(guardProductionMode)
  return baseloadOperationModes[0]?.powerConsumption ?? 0
}

const basicBaseLoadError = 'Please check the base load assets in the Assets configuration.'

export const validateBaseLoadInAssets = (assets: AssetSnapshot[]) => {
  const baseLoadAssets = assets.filter((asset) => asset.type === AssetType.BaseLoad)
  if (baseLoadAssets.length === 0)
    throw new Error(`Plant has no base load specified. ${basicBaseLoadError}`)
  if (baseLoadAssets.length > 1)
    throw new Error(`Plant has more than 1 base load asset specified. ${basicBaseLoadError}`)

  const baseloadOperationModes = baseLoadAssets[0].operationModes.filter(
    (operationMode) =>
      operationMode.type === OperationModeType.Production && operationMode.status !== Status.Deleted
  )
  if (baseloadOperationModes.length === 0)
    throw new Error(
      `Base load asset has no valid production operation modes with a base load value. ${basicBaseLoadError}`
    )
  if (baseloadOperationModes.length > 1)
    throw new Error(
      `Base load asset has more than one production operation mode. ${basicBaseLoadError}`
    )
}

export const getAssetsFromHistoryByDate = (
  assetsHistory: AssetsHistory,
  to: Moment
): AssetSnapshot[] => {
  return Object.values(assetsHistory)
    .map((assetHistory) => getRelevantHistoryEntry(assetHistory, to))
    .filter((entry): entry is AssetSnapshot => Boolean(entry))
}

export const getActiveAssets = (assets: AssetSnapshot[]) =>
  assets.filter((asset) => asset.status !== Status.Deleted)

export const getBaseLoadPowerConsumptionFromHistoryAssets = (
  assetsHistory: AssetsHistory,
  date: Moment
) => {
  const assets = getAssetsFromHistoryByDate(assetsHistory, date)
  const activeAssets = getActiveAssets(assets)
  return getBaseLoadPowerConsumptionFromAssets(activeAssets)
}

export const getHistoryAssetsById = (assetsHistory: AssetsHistory, date: Moment) => {
  const assets = getAssetsFromHistoryByDate(assetsHistory, date)
  const activeAssets = getActiveAssets(assets)
  return toRecord(activeAssets, 'id')
}
